/**
 * Maps the errno returned by `POST /api/v1/scanners/{scannerId}/{tid}/scanned` to the reasons we
 * name on the error card.
 *
 * VALID ONLY FOR THAT ENDPOINT. The 4000x range is reused with completely different meanings
 * elsewhere -- on initializeScan, 40003 is "scanner not activated" and 40004 is "scanner needs a
 * reset" (see mixins/scanner-error.js). Never feed an initializeScan errno in here, and never feed
 * a /scanned errno into getScannerErrorHint.
 *
 * The codes enumerate the combinations of three quality flags -- H (hair covers the neck),
 * U (upper body clothing not form-fitting) and L (lower body clothing not form-fitting):
 *
 *   40001 H     40002 U     40003 L     40004 H+U
 *   40005 U+L   40006 H+L   40007 H+U+L
 *
 * NOT a bitmask: a bitmask reader (H=1, U=2, L=4) would get 40005 and 40006 backwards. Hence the
 * lookup table.
 *
 * Each flag gets its own tile on the card, so the card shows exactly what the backend found -- one
 * reason renders as a single large badge, two or more as a grid. The one exception is U+L, which
 * collapses into the single 'outfit' reason ("your outfit is too loose") instead of a top + bottom
 * pair: that is a deliberate design call, not an oversight, so 40007 comes out as H + outfit.
 */

const REASONS_BY_ERRNO = Object.freeze({
  40001: Object.freeze(['hair']), // H
  40002: Object.freeze(['top']), // U
  40003: Object.freeze(['bottom']), // L
  40004: Object.freeze(['hair', 'top']), // H+U
  40005: Object.freeze(['outfit']), // U+L -- one tile by design, not top + bottom
  40006: Object.freeze(['hair', 'bottom']), // H+L
  40007: Object.freeze(['hair', 'outfit']), // H+U+L
});

/**
 * @param {number|string} errno
 * @returns {string[]|null} the reasons to render, in flag order (H, then U/L), or null when this
 *   errno is none of our business -- unknown codes, 200, and the client-side 1001/1002/1003 all
 *   land here so the caller can fall through to the generic error path unchanged. Never an empty
 *   array: a recognised errno always names at least one reason.
 */
export default errno => REASONS_BY_ERRNO[Number(errno)] || null;
